// HeadingBlockEditor — plain-text heading H1–H3, with alignment switch.
//
// Heading text is plain (no inline formatting) — that's intentional in
// the block schema (text: string, not html). Keeps card-level layout
// predictable on the published page.

import type { HeadingBlock, Block, Align } from "../../../lib/blocks";
import { t } from "../theme";

export interface HeadingBlockEditorProps {
  block: HeadingBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

const SIZE_FOR_LEVEL: Record<1 | 2 | 3, number> = { 1: 36, 2: 28, 3: 22 };

export function HeadingBlockEditor({
  block,
  onChange,
  isSelected,
}: HeadingBlockEditorProps) {
  return (
    <div>
      {isSelected && (
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 6,
            fontFamily: t.sans,
          }}
        >
          {([1, 2, 3] as const).map((lvl) => (
            <button
              key={lvl}
              type="button"
              onClick={() => onChange({ level: lvl } as Partial<HeadingBlock>)}
              style={pillStyle(block.level === lvl)}
            >
              H{lvl}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["left", "center", "right"] as Align[]).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onChange({ align: a } as Partial<HeadingBlock>)}
              style={pillStyle((block.align || "left") === a)}
            >
              {a}
            </button>
          ))}
        </div>
      )}
      <input
        type="text"
        value={block.text}
        onChange={(e) => onChange({ text: e.target.value } as Partial<HeadingBlock>)}
        placeholder={`Heading ${block.level}`}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "4px 0",
          background: "transparent",
          border: "none",
          outline: "none",
          fontFamily: t.serif,
          fontSize: SIZE_FOR_LEVEL[block.level],
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: t.text,
          textAlign: block.align || "left",
          lineHeight: 1.2,
        }}
      />
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    fontFamily: t.sans,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}
